<!--
  Device Tree Component (updated ztree)

  @since: 2021-04-13
  @author: mk.ko
-->

<template>
  <div class="content-group">

    <div v-if="!disableSearch"
         class="contents-row marB5">
      <div class="inline-group">
<!--        style="width:calc(100% - 33px);"-->
        <q-search v-model="searchText"
                  ref="inputSearch"
                  class="bs bs-q-search bs-q-search-default"
                  :style="searchTextStyle"
                  @input="onSearch(true)"
        />

<!--        :componentClass="'bs bs-q-btn-xs bs-q-btn-flat-type bs-q-btn-filter'"-->
        <q-dropdown-select v-if="isEnableFilterDeviceType"
                           v-model="searchDeviceModelType"
                           :options="deviceTypeOptions"
                           :icon-class="deviceTypeStyle"
                           @change="onSearch"
        >
        </q-dropdown-select>

        <q-btn color="primary"
               outline="outline"
               class="bs bs-q-btn-sm bs-q-btn-icon-sm bs-q-btn-line"
               @click="reload">
          <q-icon class="bs-q-btn-icon bs-q-btn-icon-refresh-sm" />
          <tool-tip :message="$t('cmn.refresh')"/>
        </q-btn>
      </div>
    </div>

    <div class="contents-row ps-q-treeBlock">

      <!-- target-scroll: q-scroll-area 중첩 시, 이벤트 버블링 방어용 -->
      <div class="device-tree-scroll-area target-scroll"
           :style="deviceScrollStyle"
           @wheel="onWheel($event)"
           @scroll="onScroll"
      >
<!--        :class="[{ 'disabled': isReadOnly }]"-->
        <z-tree ref="deviceTree"
                :nodes="treeData"
                :setting="treeSettings"
                :isReadOnly="isReadOnly"
                :auto-render="autoRender"
                @onClick="onClick"
                @beforeCheck="beforeCheckHandler"
                @onCheck="onCheck"
                @onCreated="handleCreated"
                @onRightClick="onRightClick"
        >
          <context-menu ref="contextMenu"
                        slot="context-menu"
                        manual-open
                        :isReadOnly="isReadOnly"
                        :disable-context-menu="disableContextMenu"
                        :custom-menu="customMenu"
                        :menuKey="menuKey"
                        @showContextMenu="onShowContextMenu"
                        @hideContextMenu="onHideContextMenu"
                        @ping="onContextMenuEvent('ping')"
                        @traceroute="onContextMenuEvent('traceroute')"
                        @guiExec="onContextMenuEvent('guiExec')"
          ></context-menu>
        </z-tree>

      </div>
    </div>

    <template v-if="!disableSelectedCount">
      <div class="contents-row">
        <div class="pagination-sum">{{ `${$t('cmn.selectedDeviceCount')} : ${$t('cmn.deviceCount',{count: selectedDeviceCount})}` }}</div>
      </div>
    </template>

  </div>
</template>

<script>
  import { createNamespacedHelpers } from 'vuex'

  // components
  import ZTree from '~/plugins/zTree/zTree'
  import DeviceContextMenu from '~/components/pageComponents/common/deviceContextMenu'
  import QDropdownSelect from '~/components/control/QDropdownSelect'

  // vue-modules
  // import Vue from 'vue'
  import qBtn from './button/QBtn'

  // utils
  import { getDeviceModel } from '~/utils/dashboard'
  // import { isDeviceGroupType, isSlaveNode } from '~/utils/common'
  import { isDeviceGroupType } from '~/utils/common'
  import { getErrorCodeInfo } from '~/utils/error-code'
  import { renderTooltip, destroyTooltip } from '~/utils/toolTipUtils'

  const filterHelpers = createNamespacedHelpers('common/filter')

  export default {
    name: 'DeviceTree',
    components: {
      'z-tree': ZTree,
      'context-menu': DeviceContextMenu,
      QDropdownSelect
    },
    props: {
      isReadOnly: {
        type: Boolean,
        default: false
      },

      // 그룹만 로딩할지 여부
      onlyGroupYn: {
        type: Boolean,
        default: false
      },
      // 통합 설정 가능한 장비만 로딩할지 여부
      onlyConfigYn: {
        type: Boolean,
        default: true
      },
      // TAMS 장비 로딩할지 여부
      tamsYn: {
        type: Boolean,
        default: false
      },
      // 슬레이브 장비 로딩할지 여부
      slaveYn: {
        type: Boolean,
        default: false
      },
      // 모니터링 노드 제외 여부
      exceptMonNodeYn: {
        type: Boolean,
        default: false
      },
      // PAMS 대상 장비만 로딩할지 여부
      onlyPolicyMngYn: {
        type: Boolean,
        default: false
      },
      // SD-WAN 라이선스 대상장비 여부
      onlySdwanLicYn: {
        type: Boolean,
        default: false
      },
      // TAMS 장비만 로딩할지 여부
      onlyTamsYn: {
        type: Boolean,
        default: false
      },
      // 방화벽 미지정 장비 및 장비그룹만 로딩할지 여부 (NGF Only)
      onlyUnregisteredYn: {
        type: Boolean,
        default: false
      },

      height: {
        type: String,
        default: '100%'
      },
      minHeight: {
        type: String
      },
      toggleTreeButton: {
        type: Boolean,
        default: false
      },

      // 단일 선택 시 다중 선택 비활성화
      selectedMultiple: {
        type: Boolean,
        default: false
      },
      enableCheckBox: {
        type: Boolean,
        default: false
      },
      defaultSelectedDevice: {
        type: [ Array ],
        validator: value => {
          return value.every(item => typeof item === 'number')
        },
        default: () => []
      },
      preventClick: {
        type: Boolean,
        default: false
      },
      checkBoxType: {
        type: Object,
        default () {
          return {Y: 'ps', N: 'ps'}
        }
      },
      filteredRenderingOnly: {
        type: Boolean,
        default: true
      },
      disableSelectedCount: {
        type: Boolean,
        default: false
      },
      parentNodeKey: {
        type: String,
        default: 'parent_device_id'
      },
      nodeKey: {
        type: String,
        default: 'device_id'
      },
      nodeNameKey: {
        type: String,
        default: 'device_uname'
      },
      disableSearch: {
        type: Boolean,
        default: false
      },
      disableDeviceTypeFilter: {
        type: Boolean,
        default: false
      },
      customNameTemplate: {
        type: Function,
        default: _.noop()
      },
      defaultExpandAll: {
        type: Boolean,
        default: false
      },
      autoRender: {
        type: Boolean,
        default: true
      },
      customContextMenu: {
        type: Array,
        default: () => []
      },
      externalDisableChecker: {
        type: Function,
        default: _.noop()
      },
      disableContextMenu: {
        type: Boolean,
        default: false
      },

      enableManualData: {
        type: Boolean,
        default: false
      },
      manualData: {
        type: Array,
        default: () => []
      },
      disableGroupCount: {
        type: Boolean,
        default: false
      },
      beforeClickChecker: {
        type: Function,
        default: _.noop()
      },
      disableSearchDeviceType: {
        type: Boolean,
        default: false
      },
      // BLUEMAX 관리 장비 필터링
      bluemaxModelType: {
        type: String,
        default: '',
        validator: (v) => {
          let validation = true
          _.forEach(v.split(','), val => {
            if (!['ngf', 'ips', 'wips', 'mf2', 'mfi', 'mfd', 'ads', ''].includes(val.trim())) {
              validation = false
              return false
            }
          })
          return validation
        }
      },

      // 메뉴 트리 (특정 메뉴 내 조회 옵션 의존 기능)
      treeType: {
        type: Number,
        default: 0
      },
      menuId: {
        type: Number,
        default: 0
      },
      menuSubId: { // 메뉴내에 Tab 구분시 Sub Id만 받아서 처리 가능하도록 추가함.
        type: Number,
        default: void 0
      },
      targetId: {
        type: Number,
        default: 0
      },
      menuKey: {
        type: String,
        default: ''
      },
      // 하위 TAMS 타입 (true: 상/하위, false: 상위)
      deviceSubType: {
        type: Boolean,
        default: () => true
      }
      // disableAutoCheck: {
      //   type: Boolean,
      //   default: false
      // },
      // disableParentAutoCheck: {
      //   type: Boolean,
      //   default: false
      // },
      // disableChildAutoCheck: {
      //   type: Boolean,
      //   default: false
      // }
      // tickStrategy: {
      //   type: String,
      //   default: 'none' // strict 체크박스 open
      // }
    },
    directives: {},
    mixins: [],
    data () {
      return {
        treeData: [],
        treeObj: void 0,

        searchText: '',
        prevCheckedItem: [],
        checkedItem: [],
        selectedItem: [],
        nodeData: [],
        nodeList: [],
        nodeMap: {},

        searchDeviceModelType: '-1'
      }
    },
    computed: {
      ...filterHelpers.mapState([
        'devicesTreeData'
      ]),
      numberSearchDeviceModelType () {
        return Number(this.searchDeviceModelType)
      },
      searchTextStyle () {
        try {
          if (this.isEnableFilterDeviceType) {
            return {
              width: `calc(100% - 72px)`
            }
          } else {
            return {
              width: `calc(100% - 33px)`
            }
          }
        } catch (e) {
          return {
            width: `calc(100% - 72px)`
          }
        }
      },
      isEnableFilterDeviceType () {
        try {
          return !this.onlyGroupYn && !this.disableSearchDeviceType
        } catch (e) {
          return false
        }
      },
      deviceTypeOptions () {
        let res = [
          {
            value: '-1',
            label: this.$t('cmn.all'),
            icon: 'bs-q-btn-flat-ico-ha-all'
          },
          {
            value: '20',
            label: 'NGF',
            icon: 'bs-q-btn-flat-ico-NGF'
          },
          {
            value: '21',
            label: 'IPS',
            icon: 'bs-q-btn-flat-ico-IPS'
          },
          {
            value: '22',
            label: 'WIPS',
            icon: 'bs-q-btn-flat-ico-WIPS'
          },
          {
            value: '23',
            label: 'ADS',
            icon: 'bs-q-btn-flat-ico-ADS'
          },
          {
            value: '9',
            label: 'MF2',
            icon: 'bs-q-btn-flat-ico-MF2'
          },
          {
            value: '10',
            label: 'MFI',
            icon: 'bs-q-btn-flat-ico-MFI'
          },
          {
            value: '11',
            label: 'MFD',
            icon: 'bs-q-btn-flat-ico-MFD'
          }
        ]
        if (this.tamsYn) {
          res.splice(1, 0, {
            value: '0',
            label: 'TAMS',
            icon: 'bs-q-btn-flat-ico-TAMS'
          })
        }

        return res
      },
      deviceData () {
        try {
          if (this.enableManualData) {
            return this.manualData
          } else {
            return this.devicesTreeData
          }
        } catch (e) {
          return this.devicesTreeData
        }
      },
      ticked () {
        try {
          return _.filter(this.checkedItem, node => this.checkDeviceNode(node))
        } catch (e) {
          return []
        }
      },
      selected () {
        try {
          return _.filter(this.selectedItem, node => this.checkDeviceNode(node))
        } catch (e) {
          return []
        }
      },
      deviceScrollStyle () {
        try {
          const res = {
            height: this.height
          }
          if (_.get(this.minHeight, ['length'], 0) > 0) {
            res.minHeight = this.minHeight
          }

          return res
        } catch (e) {
          return {}
        }
      },
      selectedDeviceCount () {
        try {
          return this.enableCheckBox ? this.ticked.length : this.selected.length
        } catch (e) {
          return 0
        }
      },
      isStrictMode () {
        try {
          return this.chkboxType === {Y: '', N: ''}
        } catch (e) {
          return false
        }
      },
      // chkboxType () {
      //   let res = {Y: 'ps', N: 'ps'}
      //   try {
      //     if (this.disableAutoCheck || this.disableParentAutoCheck || this.disableChildAutoCheck) {
      //       res = {Y: '', N: ''}
      //       if (this.disableParentAutoCheck) {
      //         res.Y += 'p'
      //         res.N += 'p'
      //       }
      //       if (this.disableChildAutoCheck) {
      //         res.Y += 's'
      //         res.N += 's'
      //       }
      //     }
      //   } catch (e) {}
      //   return res
      // },
      isSearched () {
        try {
          return _.get(this.searchText, ['length'], 0) > 0 || this.numberSearchDeviceModelType !== -1
        } catch (e) {
          return false
        }
      },
      treeSettings () {
        return {
          view: {
            showIcon: true,
            dblClickExpand: true,
            selectedMulti: this.selectedMultiple,
            nodeClasses: (treeId, treeNode) => {
              let res = {add: [], remove: []}
              try {
                if (treeNode.disable) {
                  res.add.push('highlight_disable')
                  res.remove.push('highlight_disable')
                }

                // if (this.isSearched) {
                //   if (this.filterCustom(treeNode)) {
                //     res.add = [...res.add, 'highlight', 'highlight_alt']
                //     res.remove = [...res.remove, 'highlight', 'highlight_alt']
                //   }
                // }
              } catch (e) {}
              return res
            },
            showTitle: false,
            // addHoverDom: this.renderTooltip
            // removeHoverDom: this.destroyTooltip
            addDiyDom: this.renderTooltipTemplate,
            // addDiyDom: this.renderTemplate
            // addDiyDom: this.renderTreeButton // 가능한 사용 금지. (성능 영향)
            autoCancelSelected: false
          },
          check: {
            enable: this.enableCheckBox,
            chkboxType: this.checkBoxType
          },
          data: {
            keep: {
              leaf: false,
              parent: false
            },
            key: {
              name: this.nodeNameKey
            },
            simpleData: {
              enable: true,
              pIdKey: this.parentNodeKey,
              idKey: this.nodeKey,
              rootPId: null
            },
            render: {
              name: this.customNameTemplate ? this.customNameTemplate : this.nameTemplate
            }
          },
          callback: {
            beforeCheck: (treeId, treeNode) => {
              if (isDeviceGroupType(treeNode.device_type) && !this.onlyGroupYn) {
                return treeNode.count > 0
              }
              return true
            },
            beforeClick: (treeId, treeNode) => {
              if (this.preventClick) {
                return false
              }
              if (typeof this.beforeClickChecker === 'function') {
                const beforeClick = this.beforeClickChecker(treeNode)
                return beforeClick
              }
              return true
            }
          }
        }
      },
      customMenu () {
        try {
          const isExistCustomMenu = _.get(this.customContextMenu, ['length'], 0) > 0
          let res = [
            {
              type: 'reload',
              label: this.$t('cmn.refresh'),
              click: (item) => {
                this.reload()
              }
            }
          ]

          if (isExistCustomMenu) {
            res = _.concat(this.customContextMenu, res)
          }
          return res
        } catch (e) {
          return []
        }
      },
      filterTreeData () {
        try {
          // this.searchText
          // _.find(state.bgpNetworkList, {id: item.id})
          return _.find(this.treeData, {[this.nodeNameKey]: this.searchText})
        } catch (e) {
          return []
        }
      },
      treeDataCount () {
        try {
          return _.get(this.treeData, ['length'], 0)
        } catch (e) {
          return 0
        }
      },
      deviceTypeStyle () {
        try {
          let res = 'q-ddl-select-border'
          if (this.numberSearchDeviceModelType !== -1) {
            const model = getDeviceModel(this.numberSearchDeviceModelType)
            res += ` bs-q-btn-flat-ico-${model}`
          } else {
            res += ' bs-q-btn-flat-ico-ha-all'
          }
          return res
        } catch (e) {
          return ''
        }
      }
    },
    methods: {
      ...filterHelpers.mapMutations([
        'initStore'
      ]),
      ...filterHelpers.mapActions([
        'getDevicesTreeData'
      ]),
      initData () {
        try {
          this.checkedItem = []
          this.selectedItem = []
          this.nodeData = []
          this.nodeList = []

          this.initSearchParams()
          this.$emit('initTree')
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (initData)]', e)
        }
      },
      initSearchParams () {
        try {
          this.searchText = ''
          this.searchDeviceModelType = '-1'
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (initSearchParams)]', e)
        }
      },
      reload (isLoading = true) {
        try {
          this.initData()

          this.loadTreeData(void 0, isLoading, true)
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (reload)]', e)
        }
      },
      async getData () {
        try {
          if (_.get(this.deviceData, ['length'], 0) === 0) {
            await this.loadTreeData(void 0, true, true)
          }
          return this.deviceData
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (getData)]', e)
        }
      },
      async getTreeData (arrayType = false) {
        try {
          return arrayType ? this.nodeList : this.nodeData
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (getData)]', e)
        }
      },
      async loadTreeData (selectedItem, isLoading = true, forceReload = false) {
        try {
          this.initSearchParams()

          // this.isSearching = true
          // this.$loader.show()
          let resultData = _.cloneDeep(this.deviceData)

          if (!this.enableManualData) {
            const res = await this.getDevicesTreeData({
              isLoading,
              onlyGroupYn: this.onlyGroupYn,
              tamsYn: this.tamsYn,
              slaveYn: this.slaveYn,
              onlyConfigYn: this.onlyConfigYn,
              exceptMonNodeYn: this.exceptMonNodeYn,
              onlyPolicyMngYn: this.onlyPolicyMngYn,
              onlySdwanLicYn: this.onlySdwanLicYn,
              onlyTamsYn: this.onlyTamsYn,
              bluemaxModelType: this.bluemaxModelType,
              onlyUnregisteredYn: this.onlyUnregisteredYn,
              treeType: this.treeType,
              menuId: this.menuId,
              menuSubId: this.menuSubId,
              targetId: this.targetId,
              deviceSubType: this.deviceSubType
            })
            if (res.code !== 'ok') {
              this.$alertPopUp.show(res.result.msg)
              return
            } else {
              resultData = res.result
            }
          }

          const treeData = _.map(resultData, d => {
            // 그룹 카운트 업데이트용
            d.count = 0

            if (isDeviceGroupType(d.device_type)) {
              // 0: 장비 그룹, 1: Stand-Alone 장비, 2: Master 장비, 3: Slave 장비, 4: TAMS Group, 5: TAMS Master, 6: TAMS Slave, 7: TAMS Sub Root, 8 : TAMS Sub Group
              if (d.device_type === 7) {
                d.iconSkin = 'ps-icon-device ps-icon-icon-sub-tams-root padL30 ztree'
              } else if (d.device_type === 8) {
                d.iconSkin = 'ps-icon-device ps-icon-icon-sub-tams-group padL30 ztree'
              } else {
                d.iconSkin = 'ps-icon-device ps-icon-icon-group padL30 ztree' // 접미어 _ico_oen/_ico_close 로 인한 마지막 class 미적용현상 방지 (ztree)
              }
            } else {
              // 장비 Icon 추가
              const model = getDeviceModel(d.device_model_type)
              const event = getErrorCodeInfo(d.event_code || 0)
              if (model.length > 0) {
                // 이벤트 코드 반영
                if (event.state === 'normal') {
                  d.iconSkin = `ps-icon-device ps-icon-device-size ps-icon-24-${event.state}-${(d.device_model_type === 12 ? 'MF2' : model.length > 0 ? model.toUpperCase() : this.$t('cmn.unknown'))}-tree padL30 ztree` // 접미어 _ico_open/_ico_close 로 인한 마지막 class 미적용현상 방지 (ztree)
                } else {
                  d.iconSkin = `ps-icon-device ps-icon-device-size ps-icon-24-${event.state}-${(d.device_model_type === 12 ? 'MF2' : model.length > 0 ? model.toUpperCase() : this.$t('cmn.unknown'))} padL30 ztree` // 접미어 _ico_open/_ico_close 로 인한 마지막 class 미적용현상 방지 (ztree)
                }
              } else { // unknown device (없다면, DB에 모델정보가 없음을 의미함)
                d.iconClass = 'ps-icon-device padL30 ztree' // 접미어 _ico_open/_ico_close 로 인한 마지막 class 미적용현상 방지 (ztree)
              }
            }

            // 사용자 정 disable
            if (d.nodeDisable !== true) {
              d.disable = false // init disable

              if (typeof this.externalDisableChecker === 'function') {
                d.disable = this.externalDisableChecker(d)
              }
            } else {
              d.disable = true
            }

            return d
          })

          this.treeData = treeData || []

          this.setSelectedNodes(selectedItem)

          if (!this.autoRender) {
            this.$refs.deviceTree.setList(this.treeData)
            this.$nextTick(() => {
              this.$refs.deviceTree.render()
            })
          }
        } catch (e) {
          // this.isSearching = false
          // this.$loader.hide()
          this.$log('[Device Tree Component GUI Error (loadTreeData)]', e)
        }
      },
      checkDeviceNode (node) {
        try {
          if (!isDeviceGroupType(node.device_type) || this.onlyGroupYn) {
            return true
          }
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (checkDeviceNode)]', e)
        }
        return false
      },
      setSelectedNodes (selectedItem) {
        try {
          if (_.get(selectedItem, ['length'], 0) > 0) {
            let items = selectedItem
            if (typeof items[0] === 'number') { // 선택된 장비 ID 키값만 있는 경우
              items = _.filter(this.treeData, node => {
                return items.indexOf(node[this.nodeKey]) !== -1
              })
            } else if (typeof items[0] === 'string') { // 선택된 장비명값만 있는 경우 (분석) // TODO: 추후 개편 시 불필요해질 기능
              items = _.filter(this.treeData, node => {
                return items.indexOf(node[this.nodeNameKey]) !== -1
              })
            } else if (typeof _.get(items, ['0', this.nodeKey]) !== 'undefined') { // 객체 타입
              items = _.filter(this.treeData, node => {
                return _.map(items, this.nodeKey).indexOf(node[this.nodeKey]) !== -1
              })
            }

            if (this.enableCheckBox) {
              this.checkedItem = items
            } else {
              this.selectedItem = items
            }
          } else {
            this.checkedItem = []
            this.selectedItem = []
          }
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (setSelectedNodes)]', e)
        }
      },
      getSelectedNodes (all = false) {
        try {
          if (all) { // 그룹 포함 모든 선택 항목
            return this.enableCheckBox ? this.checkedItem : this.selectedItem
          } else { // 택된 장비만
            return this.enableCheckBox ? this.ticked : this.selected
          }
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (getSelectedNodes)]', e)
        }
      },
      onClick (evt, treeId, treeNode, clickFlag) {
        try {
          const selectedNodes = this.$refs.deviceTree.getSelectedNodes()

          if (this.isSearched && this.selectedMultiple) {
            this.selectedItem = _.uniqBy([
              ...this.selectedItem,
              ...selectedNodes
            ], this.nodeKey)
          } else {
            this.selectedItem = selectedNodes
          }

          this.$emit('click', {
            selectedItem: this.selectedItem
          })
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (onClick)]', e)
        }
      },
      beforeCheckHandler (evt, treeId, treeNode) {
        const prevChecked = this.$refs.deviceTree.getCheckedNodes() // 기존 체크 된 트리 노드 정보
        this.prevCheckedItem = prevChecked
      },
      onCheck (evt, treeId, treeNode) {
        try {
          if (!this.isStrictMode) {
            const
              parentNodeKey = treeNode[this.parentNodeKey],
              parentNode = this.nodeMap[parentNodeKey],
              isSlaveNode = !isDeviceGroupType(parentNode) && parentNodeKey !== null // 부모가 그룹 노드가 아니면 Slave

            if (treeNode.checked === false) {
              // TODO: 개선필요
              if (isSlaveNode) { // 체크 해제된 대상이 Slave 장비인 경우 (상위 Master 장비 체크 상태 유지)
                this.parentDeviceCheck(treeNode)
              } else if (_.size(treeNode.children) > 0 && !isDeviceGroupType(treeNode.device_type)) { // 체크 해제된 대상이 Master 장비인 경우 (하위 Slave 장비 체크 상태 유지)
                this.childDeviceCheck(treeNode)
              } else if (this.isCheckedChildDepth(parentNode)) { // 체크 해제된 대상이 위치한 동일 Depth 내 장비 및 HA 장비 체크 여부 확인
                this.parentDeviceCheck(treeNode, true)
              }
            } else {
              // 체크 대상이 Slave 장비 인 경우 기존에 Master 장비가 선택되어있지 않으면 Master 장비 선택 해제
              if (isSlaveNode) {
                const parentDeviceId = parentNode.device_id
                if (!_.some(this.prevCheckedItem, ['device_id'], parentDeviceId)) {
                  this.parentDeviceCheck(treeNode, false)
                }
              }
            }
          }

          let checkedNodes = []
          if (this.isSearched && this.filteredRenderingOnly) {
            // =================================================================================
            // 1. 재귀 순회 children 판단 방식 (미완)
            // =================================================================================
            // const nodes = []
            // const isCheckable = node => (!node.isHidden && !node.nocheck && !node.chkDisabled && !node.disable)
            // const traverseChild = (node, checked = true) => {
            //   if (node.isParent) {
            //     if (isCheckable(node) && node.checked === checked) {
            //       nodes.push(node)
            //       if (this.checkBoxType !== {Y: '', N: ''}) {
            //         node.children.forEach(traverseChild)
            //       }
            //     }
            //   } else if (isCheckable(node) && node.checked === checked) {
            //     nodes.push(node)
            //   }
            // }
            //
            // const checkState = treeNode.checked
            // if (checkState) {
            //   traverseChild(treeNode)
            //   checkedNodes = _.uniqBy([
            //     ...this.checkedItem,
            //     ...nodes
            //   ], this.nodeKey)
            // } else {
            //   traverseChild(treeNode, false)
            //   const dependedNode = _.filter(nodes, node => {
            //     return !this.checkedItem.some(n => n[this.nodeKey] !== node[this.nodeKey] && n[this.nodeKey] === node[this.parentNodeKey])
            //   })
            //   checkedNodes = _.filter(this.checkedItem, node => {
            //     return !dependedNode.some(n => n[this.nodeKey] === node[this.nodeKey])
            //   })
            // }

            // =================================================================================
            // 2. getCheckedNodes 이용 (체크상태)
            // =================================================================================
            const checkState = treeNode.checked
            const nodes = this.$refs.deviceTree.getCheckedNodes(checkState)

            if (checkState) {
              checkedNodes = _.uniqBy([
                ...this.checkedItem,
                ...nodes
              ], this.nodeKey)
            } else {
              checkedNodes = _.filter(this.checkedItem, node => {
                return !nodes.some(n => n[this.nodeKey] === node[this.nodeKey])
              })
            }
          } else {
            checkedNodes = this.$refs.deviceTree.getCheckedNodes()
          }

          this.checkedItem = checkedNodes

          this.$emit('check', {
            checkedItem: this.checkedItem
          })
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (onCheck)]', e)
        }
      },
      updateSelectedState ({ afterCreated = false, focusInput = false } = {}) {
        try {
          if (_.get(this.selectedItem, ['length'], 0) > 0 || _.get(this.checkedItem, ['length'], 0) > 0) {
            if (this.enableCheckBox) {
              const
                deviceIds = _.map(this.checkedItem, this.nodeKey),
                checkedItem = this.$refs.deviceTree.getNodesByFilter(node => deviceIds.indexOf(node[this.nodeKey]) !== -1)

              for (const node of checkedItem) {
                if (!this.isStrictMode) {
                  // this.parentDeviceCheck(node)

                  const
                    parentNodeKey = node[this.parentNodeKey],
                    parentNode = this.nodeMap[parentNodeKey]

                  // 장비 유형 (0: 장비 그룹, 1: Stand-Alone 장비, 2: Master 장비, 3: Slave 장비, 4: TAMS Group, 5: TAMS Master, 6: TAMS Slave)
                  if (node.checked === false) {
                    if (!isDeviceGroupType(parentNode) ||
                      (_.size(node.children) > 0 && !isDeviceGroupType(node.device_type))
                    ) {
                      this.$refs.deviceTree.checkNode(node, true, false, false)
                      this.parentDeviceCheck(node)
                      continue
                    }
                    this.$refs.deviceTree.checkNode(node, true, !node.isParent)
                  } else {
                    this.parentDeviceCheck(node)
                  }
                } else {
                  this.$refs.deviceTree.checkNode(node, true, false)
                }
              }
            } else {
              const
                deviceIds = _.map(this.selectedItem, this.nodeKey),
                selectedItem = this.$refs.deviceTree.getNodesByFilter(node => deviceIds.indexOf(node[this.nodeKey]) !== -1)

              this._selectNode(selectedItem)
              // _.each(this.selectedItem, node => {
              //   this.$refs.deviceTree.selectNode(node, true)
              // })
            }
          }

          if (afterCreated) {
            this.$nextTick(() => {
              this.$emit('afterCreated', { nodeData: this.nodeData })
            })
          }
          if (focusInput) {
            this.$nextTick(() => {
              this.$refs.inputSearch.$el.querySelector('input').focus()
              // this.$refs.inputSearch.focus()
            })
          }
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (updateSelectedState)]', e)
        }
      },
      expandAll (toggle = true) {
        try {
          if (typeof this.treeObj !== 'undefined') {
            this.treeObj.expandAll(toggle)
          }
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (expandAll)]', e)
        }
      },
      handleCreated (ztreeObj) {
        try {
          this.treeObj = ztreeObj

          if (_.get(this.treeData, ['length'], 0) > 0) {
            if (this.defaultExpandAll) {
              this.treeObj.expandAll(true)
            }

            this.nodeData = this.treeObj.getNodes()
            this.nodeList = this.treeObj.transformToArray(this.nodeData)
            _.each(this.nodeList, node => {
              const nodeKey = node[this.nodeKey]

              if (isDeviceGroupType(node.device_type) && !this.onlyGroupYn) {
                node.count = this.getChildrenCount(node)
                if (node.count === 0) {
                  node.chkDisabled = true
                }
              }
              //
              // if (this.isReadOnly) {
              //   node.nocheck = true
              // }

              this.nodeMap[nodeKey] = node
            })
          } else {
            this.nodeData = []
            this.nodeList = []
            this.nodeMap = {}
          }

          this.refresh({ afterCreated: true })

          // this.$nextTick(() => {
          //   this.isSearching = false
          //   this.$loader.hide()
          // })
        } catch (e) {
          // this.isSearching = false
          // this.$loader.hide()
          this.$log('[Device Tree Component GUI Error (handleCreated)]', e)
        }
      },
      onRightClick ($event, treeId, treeNode) {
        try {
          if (treeNode && !treeNode.noR) {
            // const selectedData = _.assign(node, { device_id: Number(node.id) })
            this.$refs.deviceTree.selectNode(treeNode, false, true)
            this.onClick()

            this.$nextTick(() => {
              this.$refs.contextMenu.show({selectedData: treeNode, x: $event.clientX, y: $event.clientY})
            })
          } else {
            this.$refs.deviceTree.cancelSelectedNode()
          }
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (onRightClick)]', e)
        }
      },
      onContextMenuEvent (type) {
        try {
          this.$emit(type)
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (onContextMenuEvent)]', e)
        }
      },
      onShowContextMenu () {
        try {
          this.$emit('showContextMenu')
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (onShowContextMenu)]', e)
        }
      },
      onHideContextMenu () {
        try {
          this.$emit('hideContextMenu')
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (onHideContextMenu)]', e)
        }
      },
      refresh ({ afterCreated = false, refreshUpdate = false, focusInput = false } = {}) {
        try {
          if (typeof this.$refs.deviceTree !== 'undefined') {
            this.$refs.deviceTree.refresh()

            // 체크박스/선택된 값 있을 경우 업데이트
            this.$nextTick(() => {
              if (!this.enableCheckBox && (afterCreated || refreshUpdate)) {
                const selectedItem = this.getDefaultSelectedDevice()
                if (_.get(selectedItem, ['length'], 0) > 0) {
                  this.setSelectedNodes(selectedItem)
                }
              }

              this.updateSelectedState({ afterCreated, focusInput })
            })
          }
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (refresh)]', e)
        }
      },
      render (nodes) {
        try {
          if (typeof this.$refs.deviceTree !== 'undefined') {
            if (this.isSearched) {
              this.onSearch()
            } else {
              this.$refs.deviceTree.render(nodes)
            }
          }
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (render)]', e)
        }
      },

      getNodesByFilter (callback, isSingle = false) {
        try {
          if (typeof callback !== 'function') {
            return
          }

          if (typeof this.$refs.deviceTree !== 'undefined') {
            return this.$refs.deviceTree.getNodesByFilter(callback, isSingle)
          }
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (getNodesByFilter)]', e)
        }
      },

      setChkDisabled (node) {
        try {
          if (typeof node === 'undefined') {
            return
          }

          if (typeof this.$refs.deviceTree !== 'undefined') {
            if (_.get(node, ['length'], 0) > 0) {
              _.each(node, n => {
                if (n.checked) {
                  this.$refs.deviceTree.checkNode(n, false, false)
                }

                n.disable = true
                this.$refs.deviceTree.setChkDisabled(n, true)
              })
            } else {
              this.$refs.deviceTree.setChkDisabled(node, true)
            }

            this.$nextTick(() => {
              this.refresh()
            })
          }
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (setChkDisabled)]', e)
        }
      },

      checkAllNodes (checked) {
        try {
          if (typeof this.$refs.deviceTree !== 'undefined') {
            this.$refs.deviceTree.checkAllNodes(checked)
          }
          this.$nextTick(() => {
            if (checked) {
              const allCheckedItems = this.$refs.deviceTree.getSelectedNodes()
              this.setSelectedNodes(allCheckedItems)
            } else {
              this.setSelectedNodes() // 초기화
            }
          })
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (checkAllNodes)]', e)
        }
      },

      selectNode (nodeKeyItems) {
        try {
          if (_.get(nodeKeyItems, ['length'], 0) > 0) {
            let deviceIds = []
            if (typeof nodeKeyItems[0] === 'number') { // 숫자 타입 (장비 키 ID만 존재)
              deviceIds = nodeKeyItems
            } else if (typeof nodeKeyItems[0] === 'string') { // 선택된 장비명값만 있는 경우 (분석) // TODO: 추후 개편 시 불필요해질 기능
              const filterDeviceItems = _.filter(this.deviceData, node => {
                return nodeKeyItems.indexOf(node[this.nodeNameKey]) !== -1
              })
              deviceIds = _.map(filterDeviceItems, this.nodeKey)
            } else if (typeof _.get(nodeKeyItems, ['0', this.nodeKey]) !== 'undefined') { // 객체 타입
              deviceIds = _.map(nodeKeyItems, this.nodeKey)
            }

            if (_.get(deviceIds, ['length'], 0) > 0) {
              // convert z-tree node
              const selectedItem = this.$refs.deviceTree.getNodesByFilter(node => deviceIds.indexOf(node[this.nodeKey]) !== -1)
              this._selectNode(selectedItem)
            }
          }
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (selectNode)]', e)
        }
      },

      // z-tree node만 사용가능한 내부 함수
      _selectNode (node) {
        try {
          if (typeof node === 'undefined') {
            return
          }

          if (typeof this.$refs.deviceTree !== 'undefined') {
            if (_.get(node, ['length'], 0) > 0) {
              const isMultiple = _.get(node, ['length'], 0) > 1
              _.each(node, n => {
                if (!n.disable) {
                  this.$refs.deviceTree.selectNode(n, isMultiple)
                }
              })
            } else {
              this.$refs.deviceTree.selectNode(node, false)
            }
          }
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (_selectNode)]', e)
        }
      },

      renderTooltipTemplate (treeId, treeNode) {
        try {
          const $target = $(`#${treeNode.tId}_a`)
          const tooltipText = this.makeTooltipMessage(treeNode)
          $target.hover(e => {
            renderTooltip($target, tooltipText)
          }, e => {
            destroyTooltip()
          })
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (renderTooltipTemplate)]', e)
        }
      },

      // renderTemplate (treeId, treeNode) {
      //   try {
      //     const el = `#${treeNode.tId}_a`
      //     // const id = `${treeNode.tId}`
      //     // const $node = $('#' + treeNode.tId + '_a')
      //     //   const templateName = `vue-quasar-template-btn-${treeNode.tId.trim()}`
      //     //   $node.append($('<div>').attr('vue-quasar-template', templateName))
      //     // if (typeof this.$scopedSlots['name-template'] === 'function') {
      //     return new Vue({
      //       el,
      //       render (h, context) {
      //         return h(
      //           'div',
      //           {},
      //           [
      //             this.$scopedSlots['name-template']({treeNode})
      //           ]
      //         )
      //       }
      //     })
      //     // }
      //   } catch (e) {
      //     this.$log('[Device Tree Component GUI Error (renderTreeButton)]', e)
      //   }
      // },

      // 가능한 사용 금지. (성능 영향)
      renderTreeButton (treeId, treeNode) {
        try {
          // 장비 유형 (0: 장비 그룹, 1: Stand-Alone 장비, 2: Master 장비, 3: Slave 장비, 4: TAMS Group, 5: TAMS Master, 6: TAMS Slave)
          if (
            this.toggleTreeButton &&
            !treeNode.disable &&
            [0, 2, 5].indexOf(treeNode.device_type) !== -1 &&
            treeNode[this.nodeKey] !== 1
          ) {
            const $node = $('#' + treeNode.tId + '_a')
            const templateName = `vue-quasar-template-btn-${treeNode.tId.trim()}`
            $node.append($('<div>').attr('vue-quasar-template', templateName))

            const settings = {
              templateName,
              treeNode,
              data: {
                size: 'sm',
                align: 'center',
                color: 'primary',
                outline: 'outline',
                class: 'ps-q-btn-tree ps-q-btn-tree-add marL5 marB5',
                iconClass: 'ps-icon ps-q-btn-tree-icon ps-q-btn-tree-add-icon',
                labelClass: 'ps-q-btn-tree-text ps-q-btn-tree-add-text',
                label: this.$t('cmn.add')
              },
              methods: {
                click: ({item, event}) => {
                  if (item.disable) {
                    return
                  }
                  const message = `Click! (${item[this.nodeKey]})`
                  this.$q.notify({
                    icon: 'warning',
                    color: 'red-15',
                    message: message,
                    position: 'top'
                  })
                }
              }
            }

            qBtn(settings)
          }
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (renderTreeButton)]', e)
        }
      },
      clearSearchText () {
        try {
          this.searchText = ''
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (renderTreeButton)]', e)
        }
      },
      onSearch (focusInput = false) {
        try {
          // this.isSearching = true
          // this.$loader.show()

          if (this.isSearched) {
            // // TODO: hideNodes로 구현
            if (this.filteredRenderingOnly) {
              // ====================================
              // 1. Show/Hide nodes 방식 (현재 searchNode 추출안되는 상태)
              // ====================================
              // this.$refs.deviceTree.hideNodes(this.nodeList)
              //
              // // const includesName = (name = '') => name.toLowerCase().includes(this.searchText)
              // const nodeData = this.nodeData
              // const filterTree = (data = [], filter = '') => {
              //   try {
              //     return _.filter(data, node => {
              //       // const name = node[this.nodeNameKey]
              //       if (typeof node.children !== 'undefined') {
              //         return _.get(filterTree(node.children, filter), ['length'], 0) > 0 || this.filterCustom(node, filter)
              //       } else {
              //         return this.filterCustom(node, filter)
              //       }
              //     })
              //   } catch (e) {
              //     this.$log('[Device Tree Component GUI Error (onSearch)]', e)
              //     return false
              //   }
              // }
              // const searchNode = filterTree(nodeData, this.searchText)
              // const searchList = this.$refs.deviceTree.transformToArray(searchNode)
              // this.$refs.deviceTree.showNodes(searchList)

              // ====================================
              // 2. Search Node 추출 재랜더링 방식
              // ====================================
              // const nodeList = this.$refs.deviceTree.getNodesByParamFuzzy(this.nodeNameKey, this.searchText)
              //
              // let searchList = []
              // if (_.get(nodeList, ['length'], 0) > 0) {
              //   let parentNodeMap = {}, parentNodeList = []
              //
              //   const searchParentNode = list => {
              //     try {
              //       if (_.get(list, ['length'], 0) === 0) {
              //         return
              //       }
              //
              //       let res = []
              //       for (const n of list) {
              //         const parentDeviceId = n[this.parentNodeKey]
              //         if (
              //           parentDeviceId == null ||
              //           typeof parentNodeMap[parentDeviceId] !== 'undefined'
              //         ) {
              //           continue
              //         }
              //         // const parentNode = this.$refs.deviceTree.getNodeByTId(n.parentTId)
              //         const parentNode = _.find(this.treeData, {device_id: parentDeviceId})
              //         parentNodeMap[parentDeviceId] = parentNode
              //         parentNodeList.push(parentNode)
              //         res.push(parentNode)
              //       }
              //
              //       if (res.length > 0) {
              //         return searchParentNode(res)
              //       }
              //     } catch (e) {
              //       this.$log('[Device Tree Component GUI Error (searchParentNode)]', e)
              //     }
              //   }
              //
              //   searchParentNode(nodeList)
              //
              //   // 검색된 노드 + 해당 노드가 속한 부모노드(검색되지 않은) 조합
              //   searchList = _.uniqBy([
              //     ...parentNodeList,
              //     ...nodeList
              //   ], this.nodeKey)
              // }
              //
              // this.$refs.deviceTree.render(searchList)

              // ====================================
              // 3. Search Node 추출 Show/Hide nodes 방식
              //  * searchParentNode 검색 시 this.nodeList 이용
              // ====================================
              this.$refs.deviceTree.hideNodes(this.nodeList)
              // let nodeList = this.$refs.deviceTree.getNodesByParamFuzzy(this.nodeNameKey, this.searchText)
              // if (this.searchDeviceModelType !== 0) {
              //   nodeList = _.filter(nodeList, { 'device_model_type': this.searchDeviceModelType })
              // }
              const nodeList = _.filter(this.nodeList, this.filterCustom)

              let searchList = []
              if (_.get(nodeList, ['length'], 0) > 0) {
                // zTree.getNodeByTId( node.parentTId )
                let parentNodeMap = {}, parentNodeList = []

                const searchParentNode = list => {
                  try {
                    if (_.get(list, ['length'], 0) === 0) {
                      return
                    }

                    let res = []
                    for (const n of list) {
                      const parentDeviceId = n[this.parentNodeKey]
                      if (
                        parentDeviceId == null ||
                        typeof parentNodeMap[parentDeviceId] !== 'undefined'
                      ) {
                        continue
                      }
                      // const parentNode = this.$refs.deviceTree.getNodeByTId(n.parentTId)
                      const parentNode = _.find(this.nodeList, {device_id: parentDeviceId})
                      parentNodeMap[parentDeviceId] = parentNode
                      parentNodeList.push(parentNode)
                      res.push(parentNode)
                    }

                    if (res.length > 0) {
                      return searchParentNode(res)
                    }
                  } catch (e) {
                    this.$log('[Device Tree Component GUI Error (onSearch::searchParentNode)]', e)
                  }
                }

                searchParentNode(nodeList)

                // 검색된 노드 + 해당 노드가 속한 부모노드(검색되지 않은) 조합
                searchList = _.uniqBy([
                  ...parentNodeList,
                  ...nodeList
                ], this.nodeKey)

                parentNodeMap = void 0
                parentNodeList = void 0
              }

              this.$refs.deviceTree.showNodes(searchList)

              // ====================================
              // 4. Search Node 추출 Show/Hide nodes 방식
              //  * treeData finder 방식 (nodeData) -> 근데 안됨.
              // ====================================
              // let cloneNodeData = _.cloneDeep(this.nodeList)
              // this.$refs.deviceTree.hideNodes(this.nodeList)
              // const nodeList = this.$refs.deviceTree.getNodesByParamFuzzy(this.nodeNameKey, this.searchText)

              // let cloneNodeData = _.cloneDeep(this.nodeData)
              // const finder = data => {
              //   if (_.size(data) > 0) {
              //     return _.filter(data, item => {
              //       if (_.get(item, ['children', 'length'], 0) > 0) {
              //         item.children = finder(item.children)
              //         if (_.size(item.children) > 0) {
              //           return true
              //         }
              //       }
              //       if (this.filterCustom(item)) {
              //         // if (_.has(item, ['children'])) {
              //         //   item = _.omit(item, ['children'])
              //         // }
              //         return true
              //       }
              //       return false
              //     })
              //   }
              // }
              // const customNodeList = finder(_.cloneDeep(this.nodeList))
              // let searchList = this.$refs.deviceTree.transformToArray(customNodeList)
              // searchList = _.uniqBy(searchList, this.nodeKey)
              // this.$refs.deviceTree.showNodes(searchList)
            }
          } else {
            if (this.filteredRenderingOnly) {
              this.$refs.deviceTree.showNodes(this.nodeList)
              // this.$refs.deviceTree.showNodes(this.nodeData)
            }
          }

          // 검색어 터링 시, 체크박스 타입인 경우
          // 검색된 노드 하위에 부모노드가 없을 경우 체크 비활성화
          if (this.enableCheckBox && !this.onlyGroupYn) {
            _.each(this.nodeList, node => {
              if (isDeviceGroupType(node.device_type)) {
                const count = this.getChildrenCount(node, true)
                if (count === 0) {
                  node.chkDisabled = true
                } else {
                  node.chkDisabled = false
                }
              }
            })
          }

          this.$nextTick(() => {
            this.refresh({ refreshUpdate: true, focusInput })
          })
        } catch (e) {
          // this.isSearching = false
          // this.$loader.hide()
          this.$log('[Device Tree Component GUI Error (onSearch)]', e)
        }
      },
      nameTemplate (name, treeNode) {
        try {
          let res = name

          if (isDeviceGroupType(treeNode.device_type) &&
            !this.onlyGroupYn &&
            !this.disableGroupCount) {
            res = `${name} (${treeNode.count})`
          }
          return res
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (nameTemplate)]', e)
          return name
        }
      },
      filterCustom (node) {
        try {
          let
            isSearchDeviceModelType = false,
            isSearchText = false

          const isSearchAllDevice = this.numberSearchDeviceModelType === -1
          if (isSearchAllDevice) {
            isSearchDeviceModelType = true
          } else if (node.device_model_type === this.numberSearchDeviceModelType) {
            isSearchDeviceModelType = true
          } else if (isDeviceGroupType(node)) {
            isSearchDeviceModelType = true
          }

          if (
            (_.get(this.searchText, ['length'], 0) === 0) ||
            (node[this.nodeNameKey] && _.toLower(node[this.nodeNameKey]).indexOf(_.toLower(this.searchText)) > -1)
          ) {
            isSearchText = true
          }

          return isSearchDeviceModelType && isSearchText
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (filterCustom)]', e)
          return false
        }
      },
      getChildrenCount (node, checkHidden = false) {
        try {
          // 장비 유형 (0: 장비 그룹, 1: Stand-Alone 장비, 2: Master 장비, 3: Slave 장비, 4: TAMS Group, 5: TAMS Master, 6: TAMS Slave)
          if (!isDeviceGroupType(node.device_type)) { // 장비
            return 0
          } else { // 그룹
            const searchChildrenDevices = (tree, count = 0) => {
              if (tree.hasOwnProperty('children')) {
                const children = tree.children
                for (const child of children) {
                  // 장비 유형 (0: 장비 그룹, 1: Stand-Alone 장비, 2: Master 장비, 3: Slave 장비, 4: TAMS Group, 5: TAMS Master, 6: TAMS Slave)
                  if (isDeviceGroupType(child.device_type)) {
                    count = searchChildrenDevices(child, count)
                  } else if (
                    !this.filteredRenderingOnly ||
                    (this.filteredRenderingOnly && this.filterCustom(child))
                  ) {
                    if (!child.isHidden || !checkHidden) {
                      count++
                    }
                    if (child.hasOwnProperty('children')) {
                      count = searchChildrenDevices(child, count)
                    }
                  }
                }
              }
              return count
            }
            return searchChildrenDevices(node)
          }
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (getChildrenCount)]', e)
          return 0
        }
      },
      childDeviceCheck (pnode) {
        try {
          const thisNodeKey = pnode[this.nodeKey]
          const thisNode = this.nodeMap[thisNodeKey]
          if (typeof thisNode !== 'undefined') {
            let needParentCheck = false
            _.each(thisNode.children, (cnode) => {
              const childNodeKey = cnode[this.nodeKey]
              const childNode = this.nodeMap[childNodeKey]
              if (typeof childNode !== 'undefined') {
                if (_.find(this.checkedItem, { [this.nodeKey]: childNode[this.nodeKey] })) {
                  this.$refs.deviceTree.checkNode(childNode, true, false, false)
                  needParentCheck = true
                }
              }
            })

            if (needParentCheck) {
              // 상위 노드 체크 업데이트
              this.parentDeviceCheck(thisNode, true)
            }
          }
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (childDeviceCheck)]', e)
        }
      },
      parentDeviceCheck (node, forceUpdate = false) {
        try {
          const
            parentNodeKey = node[this.parentNodeKey],
            parentNode = this.nodeMap[parentNodeKey],
            thisNodeKey = node[this.nodeKey],
            thisNode = this.nodeMap[thisNodeKey]

          if (typeof parentNode !== 'undefined') {
            // TODO: 개선 요망 너무 복잡... (예상안되는 장애포인트 발생 가능성 있음)
            const
              isParentNodeDeviceGroup = isDeviceGroupType(parentNode),
              findCheckedParentNode = _.find(this.checkedItem, { [this.nodeKey]: parentNode[this.nodeKey] }),
              isCheckedParentNode = !_.isEmpty(findCheckedParentNode),
              // isCheckedSameDepthHa = this.isCheckedSameDepthHa(parentNode), // 미사용
              isCheckedChildDepth = this.isCheckedChildDepth(thisNode),
              isCheckedParentNodeChildDepth = this.isCheckedChildDepth(parentNode)

            if (
              (forceUpdate) ||
              (!isParentNodeDeviceGroup && isCheckedParentNode) ||
              (isParentNodeDeviceGroup && (isCheckedChildDepth || isCheckedParentNodeChildDepth))
            ) {
              this.$refs.deviceTree.checkNode(parentNode, true, false, false)
            } else {
              this.$refs.deviceTree.checkNode(parentNode, false, false, false)
            }

            // Root 여부 체크 (root node device_id = null)
            if (node[this.parentNodeKey] !== null) {
              this.parentDeviceCheck(parentNode, parentNode.checked)
            }
          }
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (parentDeviceCheck)]', e)
        }
      },
      // 미사용 - isCheckedChildDepth 로 대체
      isCheckedSameDepthHa (parentNode) {
        try {
          // const parentNodeKey = node[this.parentNodeKey]
          // const parentNode = this.nodeMap[parentNodeKey]
          let isChecked = false

          if (_.get(parentNode, ['children', 'length'], 0) > 0) {
            for (const child of parentNode.children) {
              if (child.checked === true) {
                isChecked = true
                break
              } else if (child.device_type === 2 || (child.device_type === 1 && _.size(child.children) > 0)) {
                for (const slave of child.children) {
                  if (slave.checked === true) {
                    isChecked = true
                    break
                  }
                }
                if (isChecked) {
                  break
                }
              }
            }
          }

          return isChecked
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (isCheckedSameDepthHa)]', e)
        }
        return false
      },
      isCheckedChildDepth (thisNode) {
        try {
          let isChecked = false

          if (_.get(thisNode, ['children', 'length'], 0) > 0) {
            // const isParentNodeDeviceGroup = isDeviceGroupType(thisNode)

            for (const child of thisNode.children) {
              if (this.onlyGroupYn) {
                if (child.checked === true) {
                  isChecked = true
                  break
                }
              } else {
                if (child.checked === true) {
                  isChecked = true
                  break
                } else if (_.size(child.children) > 0) {
                  for (const slave of child.children) {
                    if (slave.checked === true) {
                      isChecked = true
                      break
                    }
                  }
                  if (isChecked) {
                    break
                  }
                }
              }
            }
          }

          return isChecked
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (isCheckedChildDepth)]', e)
        }
        return false
      },
      makeTooltipMessage (treeNode) {
        try {
          let res = ''
          if (isDeviceGroupType(treeNode.device_type)) {
            res = treeNode.device_uname || ''
          } else {
            const model = getDeviceModel(treeNode.device_model_type)
            const event = getErrorCodeInfo(treeNode.event_code || 0)
            const messages = []
            messages.push(`${this.$t('cmn.deviceName')}: ${treeNode.device_uname}`)
            messages.push(`${this.$t('cmn.model')}: ${model.length > 0 ? model.toUpperCase() : this.$t('cmn.unknown')}`)
            messages.push(`${this.$t('da.deviceStatus_dataTable_issueUp')}: ${event.msg}`)
            res = messages.join('\n')
          }

          return res
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (makeTooltipMessage)]', e)
          return treeNode.device_uname || ''
        }
      },
      renderTooltip (treeId, treeNode) {
        try {
          const $target = $(`#${treeNode.tId}_a`)
          const tooltipText = this.makeTooltipMessage(treeNode)
          renderTooltip($target, tooltipText, 0, true)
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (renderTooltip)]', e)
        }
      },
      destroyTooltip (treeId, treeNode) {
        try {
          const $target = $(`#${treeNode.tId}_a`)
          destroyTooltip($target)
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (renderTooltip)]', e)
        }
      },
      getDefaultSelectedDevice () {
        try {
          if (!this.enableCheckBox && this.defaultSelectedDevice) {
            return this.defaultSelectedDevice
          }
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (getDefaultSelectedDevice)]', e)
        }
      },
      onWheel (e) {},
      onScroll () {},
      moveNode (targetNode, treeNode, moveType) {
        try {
          if (typeof this.$refs.deviceTree !== 'undefined') {
            this.$refs.deviceTree.moveNode(targetNode, treeNode, moveType)
          }
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (moveNode)]', e)
        }
      }
    },
    beforeCreate () {},
    created () {
      // this.initStore()
    },
    beforeMount () {},
    mounted () {
      if (this.autoRender) {
        // const selectedItem = this.getDefaultSelectedDevice()
        //
        // if (_.get(selectedItem, ['length'], 0) > 0) {
        //   this.loadTreeData(selectedItem)
        // } else {
        //   this.loadTreeData()
        // }
        this.reload()
      }

      this.$nextTick(() => {
        if (typeof this.$refs.contextMenu !== 'undefined') {
          this.$refs.contextMenu.setContextMenu()
        }
      })
    },
    beforeUpdate () {},
    updated () {},
    activated () {},
    deactivated () {},
    beforeDestroy () {
      destroyTooltip()

      // this.initStore()
      this.initData()

      this.treeData = []
      this.treeObj = void 0
    },
    destroyed () {},
    errorCaptured () {},
    watch: {
      toggleTreeButton (v) {
        try {
          this.refresh()
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (watch::toggleTreeButton)]', e)
        }
      },
      enableCheckBox () {
        try {
          this.render()
        } catch (e) {
          this.$log('[Device Tree Component GUI Error (watch::enableCheckBox)]', e)
        }
      }
    }
  }
</script>

<style scoped>
  .device-tree-scroll-area {
    overflow-y: auto;
  }
  .device-tree-scroll-area::-webkit-scrollbar { width: 10px; height: 8px; } /* TAMS */
  .device-tree-scroll-area::-webkit-scrollbar-track { background-color: #e5e5e5; }
  .device-tree-scroll-area::-webkit-scrollbar-thumb {
    background-color: #85acd6;
    /*border-radius: 3px;*/ /* TAMS */
  }
  .q-ddl-select-border {
    border: 1px solid #92a5c7 !important;
    background-color: #fff !important;
  }
</style>
